'use client';

import { useEffect, useState } from 'react';

interface SVGProgressIndicatorProps {
    totalSteps: number;
    currentStep?: number;
    onStepChange?: (step: number) => void;
    rectWidth?: number;
    spacing?: number;
}

export default function SVGProgressIndicator({
    totalSteps,
    currentStep: externalCurrentStep,
    onStepChange,
    rectWidth = 88,
    spacing = 4,
}: SVGProgressIndicatorProps) {
    const [internalCurrentStep, setInternalCurrentStep] = useState(0);

    // Use either the external current step (if provided) or the internal state
    const currentStep = externalCurrentStep !== undefined ? externalCurrentStep : internalCurrentStep;

    // Sync internal state with external prop if provided
    useEffect(() => {
        if (externalCurrentStep !== undefined) {
            setInternalCurrentStep(externalCurrentStep);
        }
    }, [externalCurrentStep]);

    // SVG dimensions and styling
    const svgWidth = 200;
    const svgHeight = 16;
    const circleRadius = 8;

    // Calculate positions
    const calculatePositions = () => {
        // Adjust rectWidth if there are many steps to ensure everything fits
        const adjustedRectWidth = totalSteps > 5 ? Math.min(rectWidth, 80) : rectWidth;

        // Total width of all elements (circles and rectangles) plus spacing
        const totalElementsWidth =
            adjustedRectWidth + (totalSteps - 1) * (circleRadius * 2) + (totalSteps - 1) * spacing;

        // Starting x position to center everything
        const startX = (svgWidth - totalElementsWidth) / 2;

        const positions = [];
        let currentX = startX;

        for (let i = 0; i < totalSteps; i++) {
            if (i === currentStep) {
                // Active step (rectangle)
                positions.push({
                    type: 'rect',
                    x: currentX,
                    width: adjustedRectWidth,
                });
                currentX += adjustedRectWidth + spacing;
            } else {
                // Inactive step (circle)
                positions.push({
                    type: 'circle',
                    cx: currentX + circleRadius,
                    r: circleRadius,
                });
                currentX += circleRadius * 2 + spacing;
            }
        }

        return positions;
    };

    const positions = calculatePositions();

    const handleStepClick = (index: number) => {
        setInternalCurrentStep(index);
        if (onStepChange) {
            onStepChange(index);
        }
    };

    return (
        <svg
            width="100%"
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            role="img"
            aria-label={`Step ${currentStep + 1} of ${totalSteps}`}
        >
            {positions.map((pos, index) => {
                if (pos.type === 'rect') {
                    return (
                        <rect
                            key={`step-${index}`}
                            x={pos.x}
                            y={0}
                            width={pos.width}
                            height={svgHeight}
                            rx={svgHeight / 2}
                            fill="#9333EA" // Vibrant purple
                            onClick={() => handleStepClick(index)}
                            className="cursor-pointer"
                        />
                    );
                }

                return (
                    <circle
                        key={`step-${index}`}
                        cx={pos.cx}
                        cy={svgHeight / 2}
                        r={pos.r}
                        fill="#EAD6F8" // Light purple
                        onClick={() => handleStepClick(index)}
                        className="cursor-pointer"
                    />
                );
            })}
        </svg>
    );
}
